import { inspectionIdentity, resourceOwnership } from "./results";
import { isResourceApplicable } from "./applicability";
import {
  MAPPING_RESOURCE_KINDS,
  adapterFileProcessingRulesEqual,
  buildAdapterFileProcessingRule,
  buildAdapterRouteId,
  fileProcessingScopeNames,
  getScopeMappings,
  mergeFileProcessingRoute,
  removeFileProcessingRoute,
  setFileProcessingSessionGoal,
  getAdapterSessionGoal,
  getResourceStepId,
} from "@/features/adapters/model/helpers";
import { readAnswer } from "@/features/adapters/model/answers";
import type {
  ResourceApplyContext,
  ResourceApplyResult,
  ResourceDriver,
  RouteResourceDefinition,
} from "./types";

/** Maps the logical mapping keys a route refers to onto the ids owned by their workflow steps. */
const getRouteMappingIds = (
  definition: RouteResourceDefinition,
  resolveResourceId: ResourceApplyContext["resolveResourceId"],
): Record<string, string> => {
  const references = [
    [definition.resource.mappingKey, definition.mappingStepId],
    ...(definition.resource.contactData
      ? [[definition.resource.contactData.mappingKey, definition.contactMappingStepId]]
      : []),
  ] as Array<[string, string | undefined]>;
  return Object.fromEntries(references.flatMap(([mappingKey]) => {
    const id = resolveResourceId(MAPPING_RESOURCE_KINDS, mappingKey);
    return id ? [[mappingKey, id]] : [];
  }));
};

const getRouteSessionGoal = (
  context: Pick<ResourceApplyContext, "answers" | "definition">,
  definition: RouteResourceDefinition,
): string | undefined => {
  const questionKey = definition.resource.sessionGoalQuestionKey;
  const answer = questionKey
    ? readAnswer(context.answers, questionKey)
    : getAdapterSessionGoal(context.definition, context.answers);
  return typeof answer === "string" && answer.trim() ? answer.trim() : undefined;
};

export const routeResourceDriver: ResourceDriver<RouteResourceDefinition, "route"> = {
  kind: "route",
  setup: "automatic",
  persistsOwnership: true,
  ensureWhenInapplicable: true,
  deferSuccess: true,
  define: (context, owner, declaration) => {
      const route = declaration.resource;
      const mappingStepId = getResourceStepId(context.definition, MAPPING_RESOURCE_KINDS, route.mappingKey);
      if (!mappingStepId) throw new Error(`Unknown mapping resource "${route.mappingKey}" in route "${route.key}".`);
      const contactMappingStepId = route.contactData
        ? getResourceStepId(context.definition, MAPPING_RESOURCE_KINDS, route.contactData.mappingKey)
        : undefined;
      const applicable = isResourceApplicable(context.answers, owner, route);
      return [{
        key: `${owner.stepId}/${route.key}`,
        stepId: owner.stepId,
        definitionKey: route.key,
        kind: "route" as const,
        label: `${fileProcessingScopeNames[route.scope]} routing`,
        applicable,
        routeKey: route.key,
        mappingStepId,
        contactMappingStepId,
        resource: route,
      }];
  },
  inspect: (context, definition, resourceId) => {
    const routeId = resourceId ?? buildAdapterRouteId(context.definition.id, definition.routeKey);
    const actual = getScopeMappings(context.release.fileProcessing, definition.resource.scope).find(
      (candidate) => candidate.id === routeId,
    );
    const owned = Boolean(resourceId && actual);
    const template = buildAdapterFileProcessingRule(
      context.definition.id,
      definition.resource,
      getRouteMappingIds(definition, context.resolveResourceId),
    );
    const expected = template && { ...template, id: routeId };
    const expectedSessionGoal = definition.resource.scope === "sessions"
      ? getRouteSessionGoal(context, definition)
      : undefined;
    const requiresSessionGoal = Boolean(definition.resource.sessionGoalQuestionKey);
    const sessions = context.release.fileProcessing?.sessions;
    const configuredSessionGoal = sessions && !Array.isArray(sessions) ? sessions.sessionGoal : undefined;
    const configured =
      adapterFileProcessingRulesEqual(actual, expected) &&
      (!requiresSessionGoal || Boolean(expectedSessionGoal && configuredSessionGoal === expectedSessionGoal));
    const complete = definition.applicable ? owned && configured : !resourceId || !actual;
    const conflict = Boolean(actual && !owned);
    const missingSelection = Boolean(resourceId && !actual);

    return {
      ...inspectionIdentity(definition),
      ownership: resourceOwnership(owned, conflict),
      exists: Boolean(actual),
      complete,
      state: conflict ? "conflict" : complete ? "ready" : actual ? "incomplete" : "missing",
      message: !definition.applicable
        ? resourceId ? "This route is no longer used by the current setup and will be removed when routing is applied." : "This route is not used by the current setup."
        : complete
          ? "Release routing points at the managed mapping and goal."
          : conflict
            ? "A route with this Adapter ID already exists. Select it before applying changes."
          : missingSelection
            ? "The selected route is missing. Select another route or clear the selection."
          : "Release routing and its session goal will be configured after mappings exist.",
      href: "/build/file-processing",
      linkLabel: "Open file processing",
      resourceId: resourceId ?? actual?.id,
      value: actual,
      inputs: [],
      completeCount: 0,
      action:
        definition.applicable && !complete && !conflict && !missingSelection
          ? { kind: "apply", label: "Apply route", stepId: definition.stepId }
          : undefined,
      dependsOn: [definition.mappingStepId, ...(definition.contactMappingStepId ? [definition.contactMappingStepId] : [])],
    };
  },
  ensure: async (context, definition, resourceId): Promise<ResourceApplyResult> => {
    const routeId = resourceId ?? buildAdapterRouteId(context.definition.id, definition.routeKey);
    if (!definition.applicable) {
      if (!resourceId) return { message: `${definition.label} is not used by this setup.` };
      const release = await context.operations.updateRelease({
        fileProcessing: removeFileProcessingRoute(
          context.release.fileProcessing,
          definition.resource.scope,
          routeId,
        ),
      });
      return {
        release,
        message: `Removed unused ${definition.label}.`,
        resourceId: null,
      };
    }

    if (resourceId && !getScopeMappings(context.release.fileProcessing, definition.resource.scope).some((candidate) => candidate.id === resourceId)) {
      throw new Error(`Selected route "${resourceId}" no longer exists in ${fileProcessingScopeNames[definition.resource.scope].toLowerCase()} routing.`);
    }

    const mappingId = context.resolveResourceId(MAPPING_RESOURCE_KINDS, definition.resource.mappingKey);
    if (!mappingId) {
      throw new Error(`Mapping "${definition.resource.mappingKey}" must exist before file-processing routing can be applied.`);
    }
    if (
      definition.resource.contactData &&
      !context.resolveResourceId(MAPPING_RESOURCE_KINDS, definition.resource.contactData.mappingKey)
    ) {
      throw new Error(
        `Mapping "${definition.resource.contactData.mappingKey}" must exist before file-processing contact loading can be applied.`,
      );
    }

    const rule = buildAdapterFileProcessingRule(
      context.definition.id,
      definition.resource,
      getRouteMappingIds(definition, context.resolveResourceId),
    );
    if (!rule) throw new Error(`Could not build file-processing route "${definition.routeKey}".`);
    if (!resourceId && getScopeMappings(context.release.fileProcessing, definition.resource.scope).some((candidate) => candidate.id === routeId)) {
      throw new Error(`Route "${routeId}" already exists. Select it before applying changes.`);
    }
    let fileProcessing = mergeFileProcessingRoute(context.release.fileProcessing, definition.resource.scope, { ...rule, id: routeId });
    const sessionGoal = definition.resource.scope === "sessions"
      ? getRouteSessionGoal(context, definition)
      : undefined;
    if (definition.resource.sessionGoalQuestionKey && !sessionGoal) {
      throw new Error(`Choose the session goal before applying route "${definition.routeKey}".`);
    }
    if (sessionGoal) {
      fileProcessing = setFileProcessingSessionGoal(fileProcessing, sessionGoal);
    }

    const release = await context.operations.updateRelease({ fileProcessing });
    return {
      release,
      message: "Applied release routing.",
      resourceId: routeId,
    };
  },
};
